import {Component, DestroyRef, inject, Input, OnDestroy, OnInit} from '@angular/core';
import {AllowanceEntity, BokkzTslDataService, GezinEninkomenPerMaand} from "@snelstart-ventures/fiscaal/domain-logic";
import {FormArray, FormControl, FormGroup} from "@angular/forms";
import {SelectFormControlItem, SharedUiFormControlsModule} from '@snelstart/ui-form-controls';
import {
  debounceTime,
  filter,
  map,
  Observable,
  pairwise,
  shareReplay,
  startWith,
  Subscription,
  switchMap,
  take, tap
} from "rxjs";
import {SharedUiDetailsModule} from '@snelstart/ui-details';
import {CommonModule} from '@angular/common';
import {SharedUiTooltipModule} from '@snelstart/ui-tooltip';
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";

type InkomenFormGroup = FormGroup<{
  maand: FormControl<number | null>;
  toeslagPartner: FormControl<boolean | null>;
  geboortedatumPartner: FormControl<Date | null>;
  bsnPartner: FormControl<string | null>;
  toetsinkomenAangever: FormControl<number | null>;
  toetsinkomenPartner: FormControl<number | null>;
  toetsvermogenAangever: FormControl<number | null>;
  toetsvermogenPartner: FormControl<number | null>;
  aangeverHeeftZorgverzekering: FormControl<boolean | null>;
  partnerHeeftZorgverzekering: FormControl<boolean | null>;
}>;

@Component({
  selector: 'snelstart-venture-fiscaal-feature-inkomen',
  templateUrl: './inkomen.component.html',
  styleUrls: ['./inkomen.component.scss'],
    imports: [
        SharedUiFormControlsModule,
        SharedUiDetailsModule,
        CommonModule,
        SharedUiTooltipModule
    ]
})
export class InkomenComponent implements OnInit, OnDestroy {

  @Input() public id!: string;
  private dataService = inject(BokkzTslDataService);
  private destroyRef = inject(DestroyRef);
  private changeSubscriptions = new Subscription();
  public hasSstIb = false;
  public year: any;

  public formGroup = new FormGroup({
    berekeningsjaar: new FormControl<number | null>(null),
    bsn: new FormControl<string | null>(null),
    geboortedatumAanvrager: new FormControl<Date | null>(null),
    aanvrager: new FormGroup({
      voorletters: new FormControl<string | null>(null),
      tussenvoegsels: new FormControl<string | null>(null),
      achternaam: new FormControl<string | null>(null),
    }),
    toeslagPartner: new FormControl<boolean | null>(null),
    toetsinkomenAangever: new FormControl<number | null>(null),
    toetsvermogenAangever: new FormControl<number | null>(null),
    aangeverHeeftZorgverzekering: new FormControl<boolean | null>(null),
    inkomensgegevens: new FormArray<InkomenFormGroup>([])
  });

  public optionsJaNee: SelectFormControlItem<boolean>[] = [
    {value: true, label: "Ja"},
    {value: false, label: "Nee"},
  ];

  readonly allowanceEntity$: Observable<AllowanceEntity> =
    this.dataService.allowanceEntity$.pipe(
      filter((data): data is AllowanceEntity => !!data?.sstRelationId),
      takeUntilDestroyed(this.destroyRef),
      shareReplay({bufferSize: 1, refCount: true})
    );

  public ngOnInit(): void {
    this.allowanceEntity$.pipe(
      take(1),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe((data) => {
      this.year = data.year;
      this.formGroup.patchValue({
        bsn: data.taxPayerFiscalNumber,
        geboortedatumAanvrager: data.toeslagen?.geboortedatumAanvrager,
        aanvrager: data.toeslagen?.aanvrager || {},
      }, {emitEvent: false});
      this.updateForm(data.toeslagen?.inkomensgegevens || []);
      this.initialiseSharedFromMonths();
      this.subscribeSharedToMonths();
    })
    this.subscribeToChanges();
  }

  ngOnDestroy(): void {
    this.saveForm();
    this.changeSubscriptions.unsubscribe();
  }

  private subscribeToChanges(): void {
    this.changeSubscriptions.unsubscribe();
    this.changeSubscriptions = new Subscription();

    const extractInputForCalculation = () =>
      this.inkomensGegevensList.getRawValue().map((item) => ({
        toetsinkomenAangever: item.toetsinkomenAangever,
        toetsvermogenAangever: item.toetsvermogenAangever,
        aangeverHeeftZorgverzekering: item.aangeverHeeftZorgverzekering,
      }));

    this.changeSubscriptions.add(
      this.formGroup.valueChanges
        .pipe(
          debounceTime(500),
          tap(() => this.saveForm()),
          map(() => extractInputForCalculation()),
          startWith(extractInputForCalculation()),
          pairwise(),
          filter(([prev, curr]) => JSON.stringify(prev) !== JSON.stringify(curr)),
          switchMap(() => this.dataService.calculateLatestAllowances()),
          takeUntilDestroyed(this.destroyRef),
        ).subscribe(),
    );
  }

  get inkomensGegevensList(): FormArray<InkomenFormGroup> {
    return this.formGroup.controls.inkomensgegevens;
  }

  createFormGroup(int: number, item?: GezinEninkomenPerMaand): FormGroup {
    return new FormGroup({
      maand: new FormControl<number | null>(item?.maand ?? int),
      toeslagPartner: new FormControl<boolean | null>(item?.toeslagPartner ?? null),
      geboortedatumPartner: new FormControl<Date | null>(item?.geboortedatumPartner ?? null),
      bsnPartner: new FormControl<string | null>(item?.bsnPartner ?? null),
      toetsinkomenAangever: new FormControl<number | null>(item?.toetsinkomenAangever ?? null),
      toetsinkomenPartner: new FormControl<number | null>(item?.toetsinkomenPartner ?? null),
      toetsvermogenAangever: new FormControl<number | null>(item?.toetsvermogenAangever ?? null),
      toetsvermogenPartner: new FormControl<number | null>(item?.toetsvermogenPartner ?? null),
      aangeverHeeftZorgverzekering: new FormControl<boolean | null>(item?.aangeverHeeftZorgverzekering ?? null),
      partnerHeeftZorgverzekering: new FormControl<boolean | null>(item?.partnerHeeftZorgverzekering ?? null),
    });
  }

  private updateForm(data : GezinEninkomenPerMaand[]): void {
    const formArray = this.inkomensGegevensList;
    const byMonth = new Map<number, GezinEninkomenPerMaand>();
    (data || []).forEach((item, idx) => {
      const m = item?.maand ?? idx + 1;
      if (m >= 1 && m <= 12) {
        byMonth.set(m, item);
      }
    });

    while (formArray.length > 12) {
      formArray.removeAt(formArray.length - 1, {emitEvent: false});
    }

    for (let m = 1; m <= 12; m++) {
      const item = byMonth.get(m);
      if (m - 1 < formArray.length) {
        const control = formArray.at(m - 1) as FormGroup;
        control.patchValue({maand: m, ...(item ?? {})}, {emitEvent: false});
      } else {
        formArray.push(this.createFormGroup(m, item), {emitEvent: false});
      }
    }
  }

  private initialiseSharedFromMonths(): void {
    const first = this.inkomensGegevensList.at(0);
    if (!first) return;
    const v = first.value;
    this.formGroup.patchValue({
      toeslagPartner: v.toeslagPartner ?? null,
      toetsinkomenAangever: v.toetsinkomenAangever ?? null,
      toetsvermogenAangever: v.toetsvermogenAangever ?? null,
      aangeverHeeftZorgverzekering: v.aangeverHeeftZorgverzekering ?? null,
    }, {emitEvent: false});
  }

  private subscribeSharedToMonths(): void {
    const c = this.formGroup.controls;
    c.toetsinkomenAangever.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(v => this.propagateToMonths('toetsinkomenAangever', v));
    c.toetsvermogenAangever.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(v => this.propagateToMonths('toetsvermogenAangever', v));
    c.aangeverHeeftZorgverzekering.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(v => this.propagateToMonths('aangeverHeeftZorgverzekering', v));
  }

  private propagateToMonths<K extends keyof InkomenFormGroup['controls']>(key: K, value: unknown): void {
    this.inkomensGegevensList.controls.forEach(month => {
      (month.controls[key] as FormControl).setValue(value, {emitEvent: false});
    });
  }

  saveForm(): void {
    this.formGroup.updateValueAndValidity({emitEvent: false});

    setTimeout(() => {

      const rawValue = this.stripEmptyStrings(this.formGroup.getRawValue());

      this.dataService.patchAllowanceEntity((currentEntity) => {
        const toeslagen: any = {
          ...currentEntity.toeslagen,
          berekeningsjaar: currentEntity.toeslagen?.berekeningsjaar ?? currentEntity.year,
          bsn: currentEntity.taxPayerFiscalNumber,
          geboortedatumAanvrager: rawValue.geboortedatumAanvrager ?? undefined,
          aanvrager: rawValue.aanvrager ?? undefined,
          inkomensgegevens: rawValue.inkomensgegevens ?? undefined,
          kot: {
            ...currentEntity.toeslagen?.kot,
            aantalWerkurenPerWeekEersteOuder: 40,
            aantalWerkurenPerWeekTweedeOuder: 40,
            eersteOuderStudeert: false,
            tweedeOuderStudeert: false,
          }
        };
        return {
          ...currentEntity,
          toeslagen,
        };
      });
    },0)
  }

  private stripEmptyStrings<T extends object>(obj: T): T {
    return Object.fromEntries(Object.entries(obj).map(([key, value]) => [key, value === '' ? undefined : value])) as T;
  }
}
